import { beToAd } from "react-lib/utils/dateUtils";
import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { BaseHeaderDetail } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import { calculateAgeAndDOB, getDataURLFromURL } from "react-lib/apps/HISV3/common/CommonInterface";

const FORM_NAME = "FormAnesDoctorFeeList";

const COLORS = {
  orange: "#F05817",
  grey: "#9F9F9F",
  black: "#000000",
};

const SvgCheckbox = (check?: boolean) => `<svg width="6" height="6">
  <rect x="1" y="5" rx="1" width="6" height="6" style="fill:transparent;stroke:#000000;stroke-width:0.5;fill-opacity:1;stroke-opacity:0.75" />
  ${
    check &&
    `<polygon style="fill:black;stroke-width:1.5;stroke:black;transform:scale(0.055) translate(10 55)" points="191.268,26.967 59.541,158.683 5.615,104.76 0,110.386 59.541,169.92 196.887,32.585"/>`
  }
  </svg>`;

const SvgArrowRight = `<svg width="30" height="6">
      <line x1="0" y1="3" x2="20" y2="3" stroke="black" stroke-width="0.8" />
      <polygon points="20,1 25,3 20,5" fill="black" />
    </svg>`;

const SvgCircle = `<svg width="4.5" height="4.5">
  <circle cx="4.5" cy="4.5" r="3.5" stroke="black" stroke-width="0.5" fill="white" />
</svg>`;

const positionItems = [
  "Jackknife",
  "Kidney",
  "Lateral Lt/Rt",
  "Lithotomy",
  "Prone",
  "Supine",
  "Trendelenburg",
];

type FormAnesDoctorFeeListProps = {
  detail: BaseHeaderDetail & {
    address?: string;
    citizen_no?: string;
    citizen_passport?: string;
    citizen_type?: string;
    doctor_number?: string;
    patient_citizen_no?: string;
    started: string;
    patientNameTH?: string;
    patientNameEN?: string;
    adrText?: string;
    genderName?: string;
    profileImage?: any;
  };
  operating?: any;
  operatingDetail?: any;
  surgeonName?: string;
};

const FormAnesDoctorFeeList = async (props: FormAnesDoctorFeeListProps) => {
  const { columns, font, fonts, fontSizes, images, lineHeights } = await CompanyLogoForm({
    font: "THSarabunNew",
    height: 40,
    form: FORM_NAME,
  });

  const profileImage = await getDataURLFromURL([{ image: props.detail.profileImage || "" }]);

  return {
    defaultStyle: {
      font: font,
      lineHeight: 1,
      fontSize: 10,
    },
    pageMargins: [0, 94, 0, 0],
    pageSize: "A4",
    pageOrientation: "landscape",
    styles: {
      fieldHeader: { alignment: "center", bold: true, fontSize: fontSizes[14.5] },
      fieldHeaderReport: { alignment: "center", bold: true, fontSize: fontSizes[16] },
      fieldHeaderDivision: { alignment: "center", fontSize: fontSizes[13] },
      fieldHeaderKey: { bold: true, fontSize: fontSizes[11] },
      fieldHeaderValue: { fontSize: fontSizes[11] },
      fieldValue: { fontSize: fontSizes[10] },
      fieldValueTable: { fontSize: fontSizes[8] },
    },
    images: {
      ...images,
      userProfile: profileImage[0].image || `${origin}/static/images/user.png`,
      bp: `${origin}/static/images/appointmentReport/bp.png`,
      pulse: `${origin}/static/images/appointmentReport/pulse.png`,
      finishAnesth: `${origin}/static/images/appointmentReport/finish_anesth.png`,
    },
    header: {
      margin: [10, 10, 10, 0],
      stack: [
        {
          table: {
            widths: ["100%"],
            body: [
              [
                {
                  margin: [5, 0, 5, 0],
                  border: [true, true, true, false],
                  columns: [
                    {
                      margin: [5, 10, 0, 0],
                      width: "22%",
                      stack: [
                        {
                          margin: [0, 0, 0, 0],
                          columns,
                        },
                        {
                          margin: [0, 0, 0, 0],
                          style: "fieldHeaderReport",
                          text: "Anesthesia Record",
                        },
                      ],
                    },
                    {
                      margin: [0, 0, 0, 5],
                      width: "*",
                      style: "fieldValue",
                      lineHeight: lineHeights[0],
                      stack: [
                        {
                          columns: [
                            {
                              columns: [
                                { text: "Date", width: 20 },
                                {
                                  margin: [0, 6, 0, 0],
                                  canvas: [
                                    {
                                      type: "line",
                                      x1: 0,
                                      y1: 3,
                                      x2: 70,
                                      y2: 3,
                                      lineWidth: 0.5,
                                      dash: { length: 1, space: 1.5 },
                                    },
                                  ],
                                  width: "*",
                                },
                              ],
                              width: "auto",
                            },
                            {
                              margin: [20, 0, 40, 0],
                              columns: [
                                {
                                  text: "ASA",
                                  alignment: "center",
                                  bold: true,
                                },
                                { text: "1", alignment: "center" },
                                { text: "2", alignment: "center" },
                                { text: "3", alignment: "center" },
                                { text: "4", alignment: "center" },
                                { text: "5", alignment: "center" },
                                { text: "6", alignment: "center" },
                                { text: "E", alignment: "center" },
                                {
                                  text: "OPD",
                                  alignment: "center",
                                  bold: true,
                                },
                                {
                                  text: "IPD",
                                  alignment: "center",
                                  bold: true,
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          columns: [
                            { text: "Pre-op Problems", width: 70 },
                            {
                              margin: [0, 6, 0, 0],
                              canvas: [
                                {
                                  type: "line",
                                  x1: 0,
                                  y1: 3,
                                  x2: 250,
                                  y2: 3,
                                  lineWidth: 0.5,
                                  dash: { length: 1, space: 1.5 },
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          columns: [
                            {
                              margin: [0, -1, 0, -1],
                              svg: SvgCheckbox(false),
                              width: 15,
                            },
                            {
                              margin: [10, 1, 0, 0],
                              text: "CONSENT Patient & Surgical site identified",
                              width: "*",
                            },
                          ],
                        },
                        {
                          columns: [
                            { text: "Pre-op Diagnosis", width: 70 },
                            {
                              margin: [0, 6, 0, 0],
                              canvas: [
                                {
                                  type: "line",
                                  x1: 0,
                                  y1: 3,
                                  x2: 250,
                                  y2: 3,
                                  lineWidth: 0.5,
                                  dash: { length: 1, space: 1.5 },
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          columns: [
                            { text: "Post-op Diagnosis", width: 70 },
                            {
                              margin: [0, 6, 0, 0],
                              canvas: [
                                {
                                  type: "line",
                                  x1: 0,
                                  y1: 3,
                                  x2: 250,
                                  y2: 3,
                                  lineWidth: 0.5,
                                  dash: { length: 1, space: 1.5 },
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          columns: [
                            { text: "Operation", width: 40 },
                            {
                              margin: [0, 6, 0, 0],
                              canvas: [
                                {
                                  type: "line",
                                  x1: 0,
                                  y1: 3,
                                  x2: 280,
                                  y2: 3,
                                  lineWidth: 0.5,
                                  dash: { length: 1, space: 1.5 },
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          margin: [0, 6, 0, 0],
                          canvas: [
                            {
                              type: "line",
                              x1: 0,
                              y1: 3,
                              x2: 320,
                              y2: 3,
                              lineWidth: 0.5,
                              dash: { length: 1, space: 1.5 },
                            },
                          ],
                          width: "*",
                        },
                        {
                          columns: [
                            { text: "Pre-med", width: 40 },
                            {
                              margin: [0, 6, 0, 0],
                              canvas: [
                                {
                                  type: "line",
                                  x1: 0,
                                  y1: 3,
                                  x2: 280,
                                  y2: 3,
                                  lineWidth: 0.5,
                                  dash: { length: 1, space: 1.5 },
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      margin: [5, 0, 5, 0],
                      width: "30%",
                      style: "fieldValue",
                      lineHeight: lineHeights[0.7],
                      stack: [
                        {
                          text: [
                            { style: "fieldHeaderKey", text: "HN: " },
                            {
                              style: "fieldHeaderValue",
                              text: `${props.detail.hn}`,
                            },
                          ],
                        },
                        {
                          text: [
                            { style: "fieldHeaderKey", text: "Name: " },
                            {
                              style: "fieldHeaderValue",
                              text:
                                props.detail.citizen_type === "T"
                                  ? `${props.detail.patientNameTH} ${
                                      props.detail.patientNameEN &&
                                      `(${props.detail.patientNameEN})`
                                    }`
                                  : `${props.detail.patientNameEN}`,
                            },
                          ],
                        },
                        {
                          columns: [
                            {
                              text: [
                                { style: "fieldHeaderKey", text: "Birthdate: " },
                                {
                                  style: "fieldHeaderValue",
                                  text: beToAd(props.detail.patient_birthdate)?.format(
                                    "D MMMM YYYY"
                                  ),
                                },
                              ],
                              width: 120,
                            },
                            {
                              alignment: "left",
                              stack: [
                                {
                                  alignment: "left",
                                  columns: [
                                    { style: "fieldHeaderKey", text: "Sex: ", width: 25 },
                                    {
                                      style: "fieldHeaderValue",
                                      text: props.detail.genderName,
                                      width: 70,
                                    },
                                  ],
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          text: [
                            { style: "fieldHeaderKey", text: "Age: " },
                            {
                              style: "fieldHeaderValue",
                              text: calculateAgeAndDOB(props.detail.patient_age).fullage_en,
                            },
                          ],
                        },
                        {
                          text: [
                            { style: "fieldHeaderKey", text: "ADR: " },
                            {
                              style: "fieldHeaderValue",
                              text: props.detail.adrText,
                            },
                          ],
                        },
                        {
                          columns: [
                            { style: "fieldHeaderKey", text: "Division: ", width: "auto" },
                            {
                              margin: [0, 6, 0, 0],
                              canvas: [
                                {
                                  type: "line",
                                  x1: 0,
                                  y1: 3,
                                  x2: 140,
                                  y2: 3,
                                  lineWidth: 0.5,
                                  dash: { length: 1, space: 1.5 },
                                },
                              ],
                              width: "*",
                            },
                          ],
                        },
                        {
                          columns: [
                            {
                              width: "auto",
                              columns: [
                                { text: "BW", width: "auto" },
                                {
                                  margin: [0, 6, 0, 0],
                                  canvas: [
                                    {
                                      type: "line",
                                      x1: 0,
                                      y1: 3,
                                      x2: 30,
                                      y2: 3,
                                      lineWidth: 0.5,
                                      dash: { length: 1, space: 1.5 },
                                    },
                                  ],
                                },
                                { text: "kg.", width: "auto" },
                              ],
                            },
                            {
                              width: "auto",
                              columns: [
                                { margin: [6, 0, 0, 0], text: "BMI", width: "auto" },
                                {
                                  margin: [0, 6, 0, 0],
                                  canvas: [
                                    {
                                      type: "line",
                                      x1: 0,
                                      y1: 3,
                                      x2: 30,
                                      y2: 3,
                                      lineWidth: 0.5,
                                      dash: { length: 1, space: 1.5 },
                                    },
                                  ],
                                },
                                { text: "Hct", width: "auto" },
                                {
                                  margin: [0, 6, 0, 0],
                                  canvas: [
                                    {
                                      type: "line",
                                      x1: 0,
                                      y1: 3,
                                      x2: 30,
                                      y2: 3,
                                      lineWidth: 0.5,
                                      dash: { length: 1, space: 1.5 },
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              width: "auto",
                              columns: [
                                { margin: [6, 0, 0, 0], text: "%ABL", width: "auto" },
                                {
                                  margin: [0, 6, 0, 0],
                                  canvas: [
                                    {
                                      type: "line",
                                      x1: 0,
                                      y1: 3,
                                      x2: 30,
                                      y2: 3,
                                      lineWidth: 0.5,
                                      dash: { length: 1, space: 1.5 },
                                    },
                                  ],
                                },
                                { text: "ml", width: "auto" },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      margin: [5, 5, 5, 0],
                      alignment: "center",
                      height: 50,
                      image: "userProfile",
                      width: 41.2,
                    },
                  ],
                },
              ],
            ],
          },
        },
      ],
    },
    content: [
      {
        margin: [10, 0, 10, 0],
        lineHeight: lineHeights[0.7],
        table: {
          widths: ["20%", "*"],
          body: [
            [
              {
                style: "fieldValue",
                stack: [
                  {
                    stack: [
                      {
                        text: "Anesthesia Techniques",
                        bold: true,
                        color: COLORS.orange,
                        alignment: "center",
                      },
                      {
                        columns: [
                          {
                            text: "GA",
                            width: "25%",
                            alignment: "left",
                          },
                          {
                            text: "TIVA",
                            width: "25%",
                            alignment: "left",
                          },
                          {
                            text: "MASK",
                            width: "25%",
                            alignment: "left",
                          },
                          {
                            text: "MAC",
                            width: "25%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            text: "SB",
                            width: "25%",
                            alignment: "left",
                          },
                          {
                            text: "C.Epi/L",
                            width: "25%",
                            alignment: "left",
                          },
                          {
                            text: "C.Epi/T",
                            width: "25%",
                            alignment: "left",
                          },
                          {
                            text: "",
                            width: "25%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            text: "Tracheostorny",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "Awake int Fiber-optic",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            text: "BPB/interscalline",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "BPB/Supraclav.",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [2, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 130,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 164,
                            y2: 3,
                            lineWidth: 1,
                          },
                        ],
                        margin: [-5, 1, 0, 1],
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        text: "Airway Type",
                        bold: true,
                        alignment: "center",
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "Spiral",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "DLT Lt/Rt",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "LMA",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "MLT",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "ETT Nasai",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "RAE Nasai",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "ETT Oral",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "RAE Oral",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "Tracheostomy",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "Tube / at ward / ICU",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "Laser tube",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "ETT test nerve",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "Face make",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            columns: [
                              { text: "Other", width: 20 },
                              {
                                margin: [0, 6, 0, 0],
                                canvas: [
                                  {
                                    type: "line",
                                    x1: 0,
                                    y1: 3,
                                    x2: 40,
                                    y2: 3,
                                    lineWidth: 0.5,
                                    dash: { length: 1, space: 1.5 },
                                  },
                                ],
                                width: "*",
                              },
                            ],
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            text: "Direct",
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            text: "Bilnd",
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [0, 0, 5, 0],
                            columns: [
                              { text: "Size", width: "auto" },
                              {
                                margin: [0, 6, 0, 0],
                                canvas: [
                                  {
                                    type: "line",
                                    x1: 0,
                                    y1: 3,
                                    x2: 40,
                                    y2: 3,
                                    lineWidth: 0.5,
                                    dash: { length: 1, space: 1.5 },
                                  },
                                ],
                              },
                              { text: "mmID.", width: "auto" },
                            ],
                            width: "50%",
                            alignment: "left",
                          },
                          {
                            margin: [5, 0, 0, 0],
                            columns: [
                              { text: "Depth", width: "auto" },
                              {
                                margin: [0, 6, 0, 0],
                                canvas: [
                                  {
                                    type: "line",
                                    x1: 0,
                                    y1: 3,
                                    x2: 40,
                                    y2: 3,
                                    lineWidth: 0.5,
                                    dash: { length: 1, space: 1.5 },
                                  },
                                ],
                              },
                              { text: "cm", width: "auto" },
                            ],
                            width: "50%",
                            alignment: "left",
                          },
                        ],
                      },
                      {
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 164,
                            y2: 3,
                            lineWidth: 1,
                          },
                        ],
                        margin: [-5, 1, 0, 1],
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        text: "Airway Management",
                        bold: true,
                        alignment: "center",
                      },
                      {
                        columns: [
                          {
                            columns: [
                              {
                                margin: [0, -1.5, 0, 0],
                                svg: SvgCheckbox(false),
                                width: 15,
                              },
                              {
                                margin: [2, 2, 0, 0],
                                text: "Mark Ventilation",
                                width: "*",
                              },
                            ],
                            width: "50%",
                          },
                          {
                            margin: [2, 2, 0, 0],
                            columns: [
                              {
                                text: "easy",
                                alignment: "center",
                              },
                              {
                                text: "Y",
                                alignment: "center",
                              },
                              {
                                text: "N",
                                alignment: "center",
                              },
                            ],
                            width: "50%",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            columns: [
                              {
                                margin: [0, -1.5, 0, 0],
                                svg: SvgCheckbox(false),
                                width: 15,
                              },
                              {
                                margin: [2, 2, 0, 0],
                                text: "Intubation",
                                width: "*",
                              },
                            ],
                            width: "50%",
                          },
                          {
                            margin: [2, 2, 0, 0],
                            columns: [
                              {
                                text: "easy",
                                alignment: "center",
                              },
                              {
                                text: "Y",
                                alignment: "center",
                              },
                              {
                                text: "N",
                                alignment: "center",
                              },
                            ],
                            width: "50%",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Remark", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 131,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        margin: [0, 6, 0, 0],
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 153,
                            y2: 3,
                            lineWidth: 0.5,
                            dash: { length: 1, space: 1.5 },
                          },
                        ],
                      },
                      {
                        margin: [0, 6, 0, 0],
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 153,
                            y2: 3,
                            lineWidth: 0.5,
                            dash: { length: 1, space: 1.5 },
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            text: "LV grade",
                            width: "auto",
                          },
                          {
                            margin: [5, 0, 5, 0],
                            columns: [
                              {
                                text: "1",
                                alignment: "center",
                              },
                              {
                                text: "2",
                                alignment: "center",
                              },
                              {
                                text: "3",
                                alignment: "center",
                              },
                              {
                                text: "4",
                                alignment: "center",
                              },
                            ],
                            width: "30%",
                          },
                          {
                            columns: [
                              { text: "DL", width: "auto" },
                              {
                                margin: [0, 6, 0, 0],
                                canvas: [
                                  {
                                    type: "line",
                                    x1: 0,
                                    y1: 3,
                                    x2: 40,
                                    y2: 3,
                                    lineWidth: 0.5,
                                    dash: { length: 1, space: 1.5 },
                                  },
                                ],
                                width: "*",
                              },
                              { text: "attempt", width: "auto" },
                            ],
                            width: "50%",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Blade Curved / Straight / Mccoy's No", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 28,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [2, 2, 0, 0],
                            text: "Adjunct Stylet / Magill's Forceps",
                            width: "*",
                          },
                        ],
                      },
                      {
                        margin: [15, 0, 0, 0],
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [2, 2, 0, 0],
                            text: "Rapid sequence",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [2, 2, 0, 0],
                            text: "Cricoid pressure",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Confirmed by:", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 105,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            width: "33%",
                            columns: [
                              {
                                margin: [0, -1.5, 0, 0],
                                svg: SvgCheckbox(false),
                                width: 15,
                              },
                              {
                                margin: [2, 2, 0, 0],
                                text: "BBS",
                                width: "*",
                              },
                            ],
                          },
                          {
                            width: "33%",
                            columns: [
                              {
                                margin: [0, -1.5, 0, 0],
                                svg: SvgCheckbox(false),
                                width: 15,
                              },
                              {
                                margin: [2, 2, 0, 0],
                                text: [
                                  "EtCO",
                                  {
                                    text: "2",
                                    fontSize: 6,
                                    relativePosition: { y: 5 },
                                    margin: [0, 2, 0, 0],
                                  },
                                ],
                                width: "*",
                              },
                            ],
                          },
                          {
                            width: "33%",
                            columns: [
                              {
                                margin: [0, -1.5, 0, 0],
                                svg: SvgCheckbox(false),
                                width: 15,
                              },
                              {
                                margin: [2, 2, 0, 0],
                                text: "FOB",
                                width: "*",
                              },
                            ],
                          },
                        ],
                      },
                      {
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 164,
                            y2: 3,
                            lineWidth: 1,
                          },
                        ],
                        margin: [-5, 1, 0, 1],
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        text: "Monitors",
                        bold: true,
                        alignment: "center",
                      },
                      {
                        columns: [
                          {
                            margin: [15, 0, 0, 0],
                            text: [
                              "EtCO",
                              {
                                text: "2",
                                fontSize: 6,
                                relativePosition: { y: 5 },
                                margin: [0, 2, 0, 0],
                              },
                            ],
                            width: "33%",
                          },
                          {
                            text: "ECG",
                            width: "33%",
                          },
                          {
                            text: "IBP",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [15, 0, 0, 0],
                            text: "Temp Naso/ESO/Skin",
                            width: "66%",
                          },
                          {
                            text: "Urine",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [15, 0, 0, 0],
                            text: "CVP",
                            width: "33%",
                          },
                          {
                            text: "NIBP",
                            width: "33%",
                          },
                          {
                            text: [
                              "SpO",
                              {
                                text: "2",
                                fontSize: 6,
                                relativePosition: { y: 5 },
                                margin: [0, 2, 0, 0],
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [15, 0, 0, 0],
                            text: "DTX",
                            width: "33%",
                          },
                          {
                            text: "ABG",
                            width: "33%",
                          },
                          {
                            text: "Hb",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [2, 2, 0, 0],
                            text: "Precordial/Esophageal stath",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [0, 2, 0, 0],
                            columns: [
                              { text: "Other", width: 20 },
                              {
                                margin: [0, 6, 0, 0],
                                canvas: [
                                  {
                                    type: "line",
                                    x1: 0,
                                    y1: 3,
                                    x2: 120,
                                    y2: 3,
                                    lineWidth: 0.5,
                                    dash: { length: 1, space: 1.5 },
                                  },
                                ],
                                width: "*",
                              },
                            ],
                          },
                        ],
                      },
                      {
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 164,
                            y2: 3,
                            lineWidth: 1,
                          },
                        ],
                        margin: [-5, 1, 0, 1],
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        text: "Reglonal Anesthesia/Nerve Block",
                        bold: true,
                        alignment: "center",
                      },
                      {
                        columns: [
                          {
                            margin: [0, -1.5, 0, 0],
                            svg: SvgCheckbox(false),
                            width: 15,
                          },
                          {
                            margin: [2, 2, 0, 0],
                            text: "RA failure",
                            width: "*",
                          },
                          {
                            margin: [-15, 6, 0, 0],
                            svg: SvgArrowRight,
                            width: 30,
                            height: 5,
                          },
                          {
                            margin: [-15, 2, 0, 0],
                            text: "GA",
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Site/approach", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 106,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Catheter no", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 111,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          {
                            margin: [0, 0, 10, 0],
                            columns: [
                              {
                                text: "Anesthesia",
                                alignment: "left",
                                width: "50%",
                              },
                              {
                                columns: [
                                  {
                                    text: "Y",
                                    alignment: "center",
                                  },
                                  {
                                    text: "N",
                                    alignment: "center",
                                  },
                                ],
                                width: "50%",
                              },
                            ],
                            width: "50%",
                          },
                          {
                            margin: [10, 0, 0, 0],
                            columns: [
                              {
                                text: "Blood",
                                alignment: "left",
                                width: "50%",
                              },
                              {
                                columns: [
                                  {
                                    text: "Y",
                                    alignment: "center",
                                  },
                                  {
                                    text: "N",
                                    alignment: "center",
                                  },
                                ],
                                width: "50%",
                              },
                            ],
                            width: "50%",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Agents", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 130,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Anesthetic level", width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 100,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        canvas: [
                          {
                            type: "line",
                            x1: 0,
                            y1: 3,
                            x2: 164,
                            y2: 3,
                            lineWidth: 1,
                          },
                        ],
                        margin: [-5, 1, 0, 1],
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        columns: [
                          { text: "Surgeon", bold: true, width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 126,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                      {
                        columns: [
                          { text: "Anesthetist", bold: true, width: "auto" },
                          {
                            margin: [0, 6, 0, 0],
                            canvas: [
                              {
                                type: "line",
                                x1: 0,
                                y1: 3,
                                x2: 117,
                                y2: 3,
                                lineWidth: 0.5,
                                dash: { length: 1, space: 1.5 },
                              },
                            ],
                            width: "*",
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                style: "fieldValueTable",
                margin: [-4, -2, -6, -2],
                stack: [
                  {
                    table: {
                      widths: ["18%", ...Array(48).fill("*"), "12%"],
                      body: [
                        [
                          {
                            border: [false, false, true, false],
                            table: {
                              widths: ["*", "30%"],
                              body: [
                                [
                                  {
                                    margin: [0, -3, 0, 0],
                                    border: [false, false, true, false],
                                    columns: [
                                      { text: "NPO Time", width: "auto" },
                                      {
                                        margin: [0, 6, 0, 0],
                                        canvas: [
                                          {
                                            type: "line",
                                            x1: 0,
                                            y1: 3,
                                            x2: 40,
                                            y2: 3,
                                            lineWidth: 0.5,
                                            dash: { length: 1, space: 1.5 },
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                  {
                                    margin: [5, -3, 5, 0],
                                    border: [false, false, false, false],
                                    text: "Time",
                                    alignment: "center",
                                  },
                                ],
                              ],
                            },
                            layout: {
                              paddingLeft: () => 0,
                              paddingRight: () => 0,
                              paddingTop: () => 0,
                              paddingBottom: () => 0,
                            },
                          },
                          ...Array.from({ length: 48 }).map((data: any, index: number) => {
                            if (index === 0) {
                              return {
                                border: [false, false, true, false],
                                text: " ",
                                colSpan: 24,
                              };
                            } else if (index === 23) {
                              return {
                                border: [false, false, false, false],
                                text: " ",
                                colSpan: 24,
                              };
                            } else {
                              return {
                                border: [false, false, false, false],
                                text: " ",
                              };
                            }
                          }),
                          {
                            margin: [0, -2, 0, 0],
                            border: [true, false, false, false],
                            text: "Total",
                            bold: true,
                            alignment: "center",
                          },
                        ],
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -3, 0, -3],
                              svg: SvgCheckbox(false),
                              width: 15,
                            },
                            {
                              margin: [2, 0, 0, 0],
                              text: [
                                "O",
                                {
                                  text: "2",
                                  fontSize: 6,
                                  relativePosition: { y: 5 },
                                  margin: [0, 2, 0, 0],
                                },
                              ],
                              width: "*",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "LPM",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: [
                                    "N",
                                    {
                                      text: "2",
                                      fontSize: 6,
                                      relativePosition: { y: 5 },
                                      margin: [0, 2, 0, 0],
                                    },
                                    "O",
                                  ],
                                  width: "*",
                                },
                              ],
                            },
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "AIR",
                                  width: "*",
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "LPM",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Sevo",
                                  width: "*",
                                },
                              ],
                            },
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Iso",
                                  width: "*",
                                },
                              ],
                            },
                            {
                              width: "*",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Des",
                                  width: "*",
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "%",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Thio",
                                  width: "*",
                                },
                              ],
                            },
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Prop",
                                  width: "*",
                                },
                              ],
                            },
                            {
                              width: "*",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [1, 5, 0, 0],
                                  canvas: [
                                    {
                                      type: "line",
                                      x1: 0,
                                      y1: 3,
                                      x2: 20,
                                      y2: 3,
                                      lineWidth: 0.5,
                                      dash: { length: 1, space: 1.5 },
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "mg.",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Succinyl",
                                  width: "*",
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "mg.",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "mg.",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Fentanyl",
                                  width: "*",
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "mcg.",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "MO",
                                  width: "*",
                                },
                              ],
                            },
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Peth",
                                  width: "*",
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "mg.",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              width: "33%",
                              columns: [
                                {
                                  margin: [0, -3, 0, -3],
                                  svg: SvgCheckbox(false),
                                  width: 15,
                                },
                                {
                                  margin: [2, 0, 0, 0],
                                  text: "Midazolam",
                                  width: "*",
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              text: "mg.",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              text: " ",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: " ",
                            },
                          ],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "LEVEL/AP",
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: [
                                "/cmH",
                                {
                                  text: "2",
                                  fontSize: 6,
                                  relativePosition: { y: 5 },
                                  margin: [0, 2, 0, 0],
                                },
                                "O",
                              ],
                            },
                          ],
                          borderTable: [false, true, false, false],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: [
                                "ETCO",
                                {
                                  text: "2",
                                  fontSize: 6,
                                  relativePosition: { y: 5 },
                                  margin: [0, 2, 0, 0],
                                },
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "mmHg",
                            },
                          ],
                          borderTable: [false, true, false, false],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "TEMP",
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "C",
                            },
                          ],
                          borderTable: [false, true, false, false],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "CVP",
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: [
                                "/cmH",
                                {
                                  text: "2",
                                  fontSize: 6,
                                  relativePosition: { y: 5 },
                                  margin: [0, 2, 0, 0],
                                },
                                "O",
                              ],
                            },
                          ],
                          borderTable: [false, true, false, false],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: [
                                "O",
                                {
                                  text: "2",
                                  fontSize: 6,
                                  relativePosition: { y: 5 },
                                  margin: [0, 2, 0, 0],
                                },
                                "SAT",
                              ],
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "%",
                            },
                          ],
                          borderTable: [false, true, false, false],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              table: {
                                margin: [0, -1, -1, 0],
                                widths: ["*", 13],
                                body: [
                                  [
                                    {
                                      border: [false, false, false, false],
                                      margin: [0, -5, 0, 0],
                                      rowSpan: 16,
                                      stack: [
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, 1, 0, 0],
                                              height: 10,
                                              image: "bp",
                                              width: 8,
                                            },
                                            {
                                              margin: [5, 0, 0, 0],
                                              text: "BP",
                                            },
                                          ],
                                        },
                                        {
                                          columns: [
                                            { margin: [0, 0, 2, 0], text: "BP", width: 10 },
                                            {
                                              margin: [0, 5, 0, 0],
                                              canvas: [
                                                {
                                                  type: "line",
                                                  x1: 0,
                                                  y1: 3,
                                                  x2: 47,
                                                  y2: 3,
                                                  lineWidth: 0.5,
                                                  dash: { length: 1, space: 1.5 },
                                                },
                                              ],
                                              width: 47,
                                            },
                                            { margin: [0, 0, 0, 0], text: "mmHg", width: "*" },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, 3, 0, 0],
                                              height: 7,
                                              image: "pulse",
                                              width: 7,
                                            },
                                            { margin: [8, 0, 2, 0], text: "Pulse", width: "auto" },
                                            {
                                              margin: [0, 5, 0, 0],
                                              canvas: [
                                                {
                                                  type: "line",
                                                  x1: 0,
                                                  y1: 3,
                                                  x2: 33,
                                                  y2: 3,
                                                  lineWidth: 0.5,
                                                  dash: { length: 1, space: 1.5 },
                                                },
                                              ],
                                              width: 33,
                                            },
                                            { margin: [0, 0, 0, 0], text: "/min", width: "*" },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, 0, 0, -3],
                                              text: " ",
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 0, 0, 0],
                                              text: "Type of ventilation",
                                              bold: true,
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, -3, 0, -3],
                                              svg: SvgCheckbox(false),
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 0, 0, 0],
                                              text: "Volume Mode",
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, -3, 0, -3],
                                              svg: SvgCheckbox(false),
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 0, 0, 0],
                                              text: "Pressure Mode",
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, -2, 0, -3],
                                              svg: SvgCheckbox(false),
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 5, 0, 0],
                                              canvas: [
                                                {
                                                  type: "line",
                                                  x1: 0,
                                                  y1: 3,
                                                  x2: 50,
                                                  y2: 3,
                                                  lineWidth: 0.5,
                                                  dash: { length: 1, space: 1.5 },
                                                },
                                              ],
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            { margin: [0, 0, 2, 0], text: "TV", width: "auto" },
                                            {
                                              margin: [0, 5, 0, 0],
                                              canvas: [
                                                {
                                                  type: "line",
                                                  x1: 0,
                                                  y1: 3,
                                                  x2: 45,
                                                  y2: 3,
                                                  lineWidth: 0.5,
                                                  dash: { length: 1, space: 1.5 },
                                                },
                                              ],
                                              width: 45,
                                            },
                                            { margin: [0, 0, 0, 0], text: "cc", width: "*" },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            { margin: [0, 0, 2, 0], text: "Rate", width: "auto" },
                                            {
                                              margin: [0, 5, 0, 0],
                                              canvas: [
                                                {
                                                  type: "line",
                                                  x1: 0,
                                                  y1: 3,
                                                  x2: 40,
                                                  y2: 3,
                                                  lineWidth: 0.5,
                                                  dash: { length: 1, space: 1.5 },
                                                },
                                              ],
                                              width: 40,
                                            },
                                            { margin: [0, 0, 0, 0], text: "/Min", width: "*" },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          width: "*",
                                          columns: [
                                            { margin: [0, 0, 2, 0], text: "PEEP", width: 20 },
                                            {
                                              margin: [0, 5, 0, 0],
                                              canvas: [
                                                {
                                                  type: "line",
                                                  x1: 0,
                                                  y1: 3,
                                                  x2: 38,
                                                  y2: 3,
                                                  lineWidth: 0.5,
                                                  dash: { length: 1, space: 1.5 },
                                                },
                                              ],
                                              width: 38,
                                            },
                                            {
                                              margin: [0, 0, 0, 0],
                                              text: [
                                                "cmH",
                                                {
                                                  text: "2",
                                                  fontSize: 6,
                                                  relativePosition: { y: 5 },
                                                  margin: [0, 2, 0, 0],
                                                },
                                                "O",
                                              ],
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, 0, 0, -3],
                                              text: "X",
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 0, 0, 0],
                                              text: "Start anesth",
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, 3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, 0, 0, -3],
                                              text: "T",
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 0, 0, 0],
                                              text: "Intubation",
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [-1, 3, 0, 1],
                                          columns: [
                                            {
                                              margin: [0, 3, 0, 0],
                                              svg: SvgCircle,
                                              width: 15,
                                            },
                                            {
                                              margin: [0, 2, 0, 0],
                                              text: "Start Op",
                                              width: "*",
                                            },
                                          ],
                                        },
                                        {
                                          margin: [0, -3, 0, 0],
                                          columns: [
                                            {
                                              margin: [0, 1, 0, 0],
                                              height: 7,
                                              image: "finishAnesth",
                                              width: 7,
                                            },
                                            {
                                              margin: [8, 0, 2, 0],
                                              text: "Finish anesth",
                                              width: "*",
                                            },
                                          ],
                                        },
                                      ],
                                    },
                                    {
                                      border: [false, false, false, false],
                                      margin: [0, -5, 0, 0],
                                      rowSpan: 16,
                                      stack: [
                                        ...[
                                          "300",
                                          "280",
                                          "260",
                                          "240",
                                          "220",
                                          "200",
                                          "180",
                                          "160",
                                          "140",
                                          "120",
                                          "100",
                                          "80",
                                          "60",
                                          "40",
                                          "20",
                                          "0",
                                        ].map((val: string, index: number) => [
                                          "",
                                          {
                                            lineHeight: 0.6,
                                            text: val,
                                            alignment: "right",
                                            margin: index === 15 ? [0, 1, 0, 0] : [0, 2, 0, 2],
                                          },
                                        ]),
                                      ],
                                    },
                                  ],
                                  ...Array.from({ length: 15 }).map((data: any, index: number) => {
                                    return [{}, {}];
                                  }),
                                ],
                              },
                            },
                          ],
                          columnsTotal: [
                            {
                              table: {
                                margin: [0, -1, -1, 0],
                                widths: ["100%"],
                                body: [
                                  [
                                    {
                                      margin: [0, 0, -1, 2],
                                      text: "Position",
                                      bold: true,
                                      alignment: "center",
                                    },
                                  ],
                                  ...positionItems.map((text, i) => [
                                    {
                                      text,
                                      alignment: "center",
                                      border: [false, false, false, false],
                                      margin: [0, 0, 0, i === positionItems.length - 1 ? 4 : 0],
                                    },
                                  ]),
                                  [
                                    {
                                      margin: [0, 0, -1, 2],
                                      text: "Site of Operation",
                                      bold: true,
                                      alignment: "center",
                                    },
                                  ],
                                  ...Array.from({ length: 3 }).map((data: any, index: number) => {
                                    return [
                                      {
                                        margin: [0, 2, 0, 0],
                                        border: [false, false, false, false],
                                        width: "*",
                                        columns: [
                                          {
                                            text: " ",
                                            width: "10%",
                                          },
                                          {
                                            margin: [0, -3, 0, -3],
                                            svg: SvgCheckbox(false),
                                            width: 15,
                                          },
                                          {
                                            margin: [0, 4, 0, 0],
                                            canvas: [
                                              {
                                                type: "line",
                                                x1: 0,
                                                y1: 3,
                                                x2: 45,
                                                y2: 3,
                                                lineWidth: 0.5,
                                                dash: { length: 1, space: 1.5 },
                                              },
                                            ],
                                            width: "*",
                                          },
                                          {
                                            text: " ",
                                            width: "10%",
                                          },
                                        ],
                                      },
                                    ];
                                  }),
                                ],
                              },
                              layout: {
                                paddingLeft: () => 0,
                                paddingRight: () => 0,
                                paddingTop: () => 0,
                                paddingBottom: () => 0,
                                hLineColor: () => COLORS.black,
                                vLineColor: () => COLORS.black,
                              },
                            },
                          ],
                          isGraft: true,
                          borderColumnsData: [false, true, true, false],
                          borderColumnsTotal: [true, true, false, false],
                          marginColumnsTotal: [-1, -1, 0, 0],
                          rowSpanColumnsTotal: 16,
                          rowSpanColumnsData: 16,
                        }),
                        ...Array.from({ length: 15 }).map((data: any, index: number) => {
                          return formGrayBorderTable({
                            isGraft: true,
                            isRowEmptyColumnsTotal: true,
                            isRowEmptyColumnsData: true,
                            borderColumnsData: [false, false, true, false],
                            ...([8, 10].includes(index) && {
                              isOrangeLine: true,
                            }),
                          });
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              text: "Control/assist/spont",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: " ",
                            },
                          ],
                          isGraft: true,
                          borderColumnsTotal: [true, false, false, false],
                          borderColumnsData: [false, true, true, true],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "Events/Symbols",
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "Total",
                              alignment: "center",
                            },
                          ],
                          borderTable: [false, true, false, false],
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "Fluids",
                              bold: true,
                              color: COLORS.orange,
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              text: " ",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              text: " ",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              text: " ",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "Blood",
                              bold: true,
                              color: COLORS.orange,
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              text: " ",
                            },
                          ],
                          columnsTotal: [
                            {
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "Urine",
                              bold: true,
                              color: COLORS.orange,
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        formGrayBorderTable({
                          columnsData: [
                            {
                              margin: [0, -1, 0, 0],
                              text: "EBL",
                              bold: true,
                              color: COLORS.orange,
                            },
                          ],
                          columnsTotal: [
                            {
                              margin: [0, -1, 0, 0],
                              text: " ",
                            },
                          ],
                          borderTable: [false, true, false, false],
                          isUnderGraft: true,
                        }),
                        [
                          {
                            margin: [-2, -1, 0, 0],
                            border: [false, true, false, false],
                            colSpan: 50,
                            table: {
                              widths: ["30%", "10%", "10%", "50%"],
                              body: [
                                [
                                  {
                                    margin: [2, 0, 2, 0],
                                    border: [false, true, true, false],
                                    stack: [
                                      {
                                        text: "Number Event",
                                        color: COLORS.orange,
                                        bold: true,
                                      },
                                      ...Array.from({ length: 5 }).map(
                                        (data: any, index: number) => {
                                          return {
                                            margin: index === 4 ? [0, 0, 0, 2] : [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: `${index + 1}.`,
                                                width: 10,
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 180,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                              },
                                            ],
                                          };
                                        }
                                      ),
                                    ],
                                  },
                                  {
                                    border: [false, true, true, false],
                                    stack: [
                                      {
                                        text: "Intraop comp",
                                        alignment: "center",
                                      },
                                      ...Array.from({ length: 5 }).map(
                                        (data: any, index: number) => {
                                          return {
                                            margin: index === 4 ? [0, 0, 0, 2] : [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -5, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                margin: [-2, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 50,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                              },
                                            ],
                                          };
                                        }
                                      ),
                                    ],
                                  },
                                  {
                                    border: [false, true, true, false],
                                    stack: [
                                      {
                                        text: "Intraop Rx",
                                        alignment: "center",
                                      },
                                      ...Array.from({ length: 5 }).map(
                                        (data: any, index: number) => {
                                          return {
                                            margin: index === 4 ? [0, 0, 0, 2] : [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -5, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                margin: [-5, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 52,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                              },
                                            ],
                                          };
                                        }
                                      ),
                                    ],
                                  },
                                  {
                                    margin: [2, 0, 2, 0],
                                    border: [false, true, false, false],
                                    stack: [
                                      {
                                        text: "Remark:",
                                        color: COLORS.orange,
                                        bold: true,
                                      },
                                      ...Array.from({ length: 5 }).map(
                                        (data: any, index: number) => {
                                          return {
                                            columns: [
                                              {
                                                margin: index === 4 ? [0, 4, 0, 2] : [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 322,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                              },
                                            ],
                                          };
                                        }
                                      ),
                                    ],
                                  },
                                ],
                                [
                                  {
                                    margin: [2, 2, 2, 4],
                                    border: [false, true, true, false],
                                    colSpan: 3,
                                    stack: [
                                      {
                                        columns: [
                                          {
                                            width: "70%",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "NMB Reversal with",
                                                bold: true,
                                              },
                                            ],
                                          },
                                          {
                                            width: "30%",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "No Reversal",
                                                bold: true,
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                      {
                                        columns: [
                                          {
                                            width: 15,
                                            text: " ",
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "1. Atropine",
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 121,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                              {
                                                text: "mg / Glyco-P",
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 121,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                              },
                                              {
                                                text: "mg",
                                                width: "auto",
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                      {
                                        width: "*",
                                        margin: [0, 0, 0, 0],
                                        columns: [
                                          {
                                            width: 15,
                                            text: " ",
                                          },
                                          {
                                            text: "2. Neostigmine",
                                            width: 40,
                                          },
                                          {
                                            margin: [0, 4, 0, 0],
                                            canvas: [
                                              {
                                                type: "line",
                                                x1: 0,
                                                y1: 3,
                                                x2: 110,
                                                y2: 3,
                                                lineWidth: 0.5,
                                                dash: { length: 1, space: 1.5 },
                                              },
                                            ],
                                            width: "auto",
                                          },
                                          {
                                            text: "mg",
                                            width: "auto",
                                          },
                                          {
                                            text: " ",
                                            width: 27,
                                          },
                                          {
                                            text: "Time",
                                            width: "auto",
                                            alignment: "right",
                                          },
                                          {
                                            margin: [0, 4, 0, 0],
                                            alignment: "right",
                                            canvas: [
                                              {
                                                type: "line",
                                                x1: 0,
                                                y1: 3,
                                                x2: 110,
                                                y2: 3,
                                                lineWidth: 0.5,
                                                dash: { length: 1, space: 1.5 },
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                      {
                                        width: "*",
                                        margin: [0, 0, 0, 0],
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Spontaneous breathing",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Eyes opened",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Strong grip/head",
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                      {
                                        width: "*",
                                        margin: [0, 0, 0, 0],
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Follow command",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Remains intubated",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Reintubation",
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                  {},
                                  {},
                                  {
                                    margin: [2, 0, 2, 4],
                                    border: [false, true, false, false],
                                    stack: [
                                      {
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "Transfer to",
                                                color: COLORS.orange,
                                                bold: true,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "PACU",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "ICU",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "WARD",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                      {
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "with",
                                                color: COLORS.orange,
                                                width: 15,
                                              },
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "ETT",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "ORAL/NASAL",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "",
                                                width: 15,
                                              },
                                              {
                                                text: "AIRWAY",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Trach",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                      {
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: " ",
                                                width: 15,
                                              },
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Cavafix Rt / Lt",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            text: " ",
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: " ",
                                                width: 15,
                                              },
                                              {
                                                text: "Depth",
                                                color: COLORS.orange,
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 40,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    lineColor: COLORS.orange,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                              {
                                                text: "cm",
                                                color: COLORS.orange,
                                                width: "*",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            text: " ",
                                          },
                                        ],
                                      },
                                      {
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: " ",
                                                width: 15,
                                              },
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "A-Line Rt / Lt",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            text: " ",
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "NG Rt / Lt",
                                                color: COLORS.orange,
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            text: " ",
                                          },
                                        ],
                                      },
                                      {
                                        columns: [
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "BP",
                                                color: COLORS.orange,
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 40,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    lineColor: COLORS.orange,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                              {
                                                text: "mmHg",
                                                color: COLORS.orange,
                                                width: "*",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "HR",
                                                color: COLORS.orange,
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 40,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    lineColor: COLORS.orange,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                              {
                                                text: "/min",
                                                color: COLORS.orange,
                                                width: "*",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: "RR",
                                                color: COLORS.orange,
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 40,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    lineColor: COLORS.orange,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                              {
                                                text: "/min",
                                                color: COLORS.orange,
                                                width: "*",
                                              },
                                            ],
                                          },
                                          {
                                            width: "*",
                                            margin: [0, 0, 0, 0],
                                            columns: [
                                              {
                                                text: [
                                                  "SPO",
                                                  {
                                                    text: "2",
                                                    fontSize: 6,
                                                    relativePosition: { y: 5 },
                                                    margin: [0, 2, 0, 0],
                                                  },
                                                ],
                                                color: COLORS.orange,
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 40,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    lineColor: COLORS.orange,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                              {
                                                text: "%",
                                                color: COLORS.orange,
                                                width: "*",
                                              },
                                            ],
                                          },
                                        ],
                                      },
                                    ],
                                  },
                                ],
                                [
                                  {
                                    margin: [-1, 0, -1, -1],
                                    border: [false, false, true, false],
                                    colSpan: 3,
                                    table: {
                                      widths: ["*", 50, "*", "*", "*", "*"],
                                      body: [
                                        [
                                          {
                                            margin: [2, 2, 0, 2],
                                            border: [false, true, true, false],
                                            columns: [
                                              {
                                                margin: [0, -3, 0, -5],
                                                svg: SvgCheckbox(false),
                                                width: 15,
                                              },
                                              {
                                                text: "Extubated",
                                              },
                                            ],
                                          },
                                          {
                                            margin: [0, 10, 0, 0],
                                            border: [false, true, true, false],
                                            text: "Time",
                                            alignment: "center",
                                            rowSpan: 2,
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: "Pt. In",
                                            alignment: "center",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: "Start AN.",
                                            alignment: "center",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: "Start Op.",
                                            alignment: "center",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, false, false],
                                            text: "Op. Finish",
                                            alignment: "center",
                                          },
                                        ],
                                        [
                                          {
                                            margin: [2, 2, 0, 2],
                                            border: [false, false, true, false],
                                            columns: [
                                              {
                                                text: "Time",
                                                width: "auto",
                                              },
                                              {
                                                margin: [0, 4, 0, 0],
                                                canvas: [
                                                  {
                                                    type: "line",
                                                    x1: 0,
                                                    y1: 3,
                                                    x2: 40,
                                                    y2: 3,
                                                    lineWidth: 0.5,
                                                    dash: { length: 1, space: 1.5 },
                                                  },
                                                ],
                                                width: "auto",
                                              },
                                            ],
                                          },
                                          {},
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, false, true, false],
                                            text: " ",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: " ",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: " ",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, false, false],
                                            text: " ",
                                          },
                                        ],
                                      ],
                                    },
                                    layout: {
                                      paddingLeft: () => 0,
                                      paddingRight: () => 0,
                                      paddingTop: () => 0,
                                      paddingBottom: () => 0,
                                    },
                                  },
                                  {},
                                  {},
                                  {
                                    margin: [-1, 0, 0, 0],
                                    border: [false, false, false, false],
                                    table: {
                                      widths: [60, 30, 30, "*"],
                                      body: [
                                        [
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: "AN. Finish",
                                            alignment: "center",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: "Total hr/min",
                                            alignment: "center",
                                            colSpan: 2,
                                          },
                                          {},
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, false, false, false],
                                            text: " ",
                                          },
                                        ],
                                        [
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: " ",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: " ",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, true, true, false],
                                            text: " ",
                                          },
                                          {
                                            margin: [0, 2, 0, 2],
                                            border: [false, false, false, false],
                                            text: " ",
                                          },
                                        ],
                                      ],
                                    },
                                    layout: {
                                      paddingLeft: () => 0,
                                      paddingRight: () => 0,
                                      paddingTop: () => 0,
                                      paddingBottom: () => 0,
                                    },
                                  },
                                ],
                              ],
                            },
                            layout: {
                              paddingLeft: () => 0,
                              paddingRight: () => 0,
                              paddingTop: () => 0,
                              paddingBottom: () => 0,
                            },
                          },
                          ...Array.from({ length: 49 }).map((data: any, index: number) => {
                            return {
                              border: [false, false, false, false],
                              text: " ",
                            };
                          }),
                        ],
                      ],
                    },
                    layout: {
                      paddingLeft: () => 0,
                      paddingRight: () => 0,
                      paddingTop: () => 0,
                      paddingBottom: () => 0,
                      hLineColor: function (i: number, node: any) {
                        if (i > 0) {
                          return COLORS.grey;
                        } else {
                          return COLORS.black;
                        }
                      },
                      vLineColor: function (i: number, node: any) {
                        if (
                          [
                            2, 3, 5, 6, 8, 9, 11, 12, 14, 15, 17, 18, 20, 21, 23, 24, 26, 27, 29,
                            30, 32, 33, 35, 36, 38, 39, 41, 42, 44, 45, 47, 48, 50, 51,
                          ].includes(i)
                        )
                          return COLORS.grey;
                        return COLORS.black;
                      },
                    },
                  },
                ],
              },
            ],
          ],
        },
      },
    ],
  };
};

export default FormAnesDoctorFeeList;

const formGrayBorderTable = (data: {
  columnsData?: object[];
  columnsTotal?: object[];
  borderTable?: boolean[];
  borderColumnsData?: boolean[];
  borderColumnsTotal?: boolean[];
  marginColumnsTotal?: number[];
  isGraft?: boolean;
  isUnderGraft?: boolean;
  isOrangeLine?: boolean;
  isRowEmptyColumnsTotal?: boolean;
  isRowEmptyColumnsData?: boolean;
  rowSpanColumnsTotal?: number;
  rowSpanColumnsData?: number;
}) => {
  const {
    columnsData = [],
    columnsTotal = [],
    borderTable = [true, true, false, false],
    borderColumnsData = [false, true, true, false],
    borderColumnsTotal = [true, true, false, false],
    marginColumnsTotal = [0, -1, 10, 0],
    isGraft = false,
    isUnderGraft = false,
    isOrangeLine = false,
    isRowEmptyColumnsTotal = false,
    isRowEmptyColumnsData = false,
    rowSpanColumnsTotal = 1,
    rowSpanColumnsData = 1,
  } = data;

  return [
    !isRowEmptyColumnsData
      ? {
          lineHeight: 0.6,
          margin: [2, -1, 0, 0],
          border: borderColumnsData || [false, true, true, false],
          columns: columnsData,
          rowSpan: rowSpanColumnsData,
        }
      : "",
    ...Array.from({ length: 48 }).map((data: any, index: number) => {
      if (isGraft) {
        return {
          lineHeight: 0.6,
          table: {
            margin: [0, 0, 0, 0],
            widths: ["100%"],
            body: [
              [
                {
                  lineHeight: 0.3,
                  margin: [0, 0, 0, 0],
                  border: [false, false, false, true],
                  text: " ",
                },
              ],
              [
                {
                  lineHeight: 0.3,
                  margin: [0, 0, 0, 0],
                  border: [false, false, false, false],
                  text: " ",
                },
              ],
            ],
          },
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
            hLineColor: () => {
              if (isOrangeLine) {
                return COLORS.orange;
              } else {
                return COLORS.grey;
              }
            },
            vLineColor: () => COLORS.grey,
          },
        };
      } else {
        return index === 0
          ? { lineHeight: 0.6, border: [false, true, false, false], text: " " }
          : [11, 23, 35].includes(index) && isUnderGraft
          ? { lineHeight: 0.6, border: [false, true, true, false], text: " " }
          : { lineHeight: 0.6, border: borderTable, text: " " };
      }
    }),
    !isRowEmptyColumnsTotal
      ? {
          lineHeight: 0.6,
          margin: marginColumnsTotal,
          border: borderColumnsTotal,
          columns: columnsTotal,
          alignment: "right",
          rowSpan: rowSpanColumnsTotal,
        }
      : "",
  ];
};
